import type { GetServerSideProps } from 'next';
import styled from 'styled-components';

import BookDescription from '@components/BookDescription';
import BookTypes from '@components/BookTypes';
import Collaborate from '@components/Collaborate';
import EditorExample from '@components/EditorExample';
import Example from '@components/Example';
import Footer from '@components/Footer';
import Gift from '@components/Gift';
import GoodToKnow from '@components/GoodToKnow';
import Navigation from '@components/Navigation';
import Reviews from '@components/Reviews';
import { backgroundColored } from '@constants/colors';
import { book, productBook, productionUrl } from '@constants/routes';
import { addBreadcrumbsJsonLd } from '@constants/seo';
import CustomHead from '@elements/CustomHead';
import { Product } from '@graphql/generated/graphql';
import {
  PageGetProductBySlugComp,
  ssrGetCollaborativeSpreads,
  ssrGetProductBySlug,
} from '@graphql/generated/page';

const StyledMain = styled.main`
  background-color: ${backgroundColored};
`;

const ProductDetails: PageGetProductBySlugComp = ({ data }) => {
  const breadcrumbs = [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Fotoboeken',
      item: 'https://belmondofoto.nl/fotoboeken',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: data?.product.title,
    },
  ];

  return (
    <>
      <CustomHead
        title={data?.product.seo.title || data?.product.title || ''}
        description={data?.product.seo.description || ''}
        canonicalUrl={`${productionUrl}${productBook(
          data?.product.slug || '',
        )}`}
      >
        <script
          type="application/ld+json"
          key="product-breadcrumbs-jsonld"
          dangerouslySetInnerHTML={addBreadcrumbsJsonLd(breadcrumbs)}
        />
      </CustomHead>
      <Navigation backgroundColor={backgroundColored} />
      <StyledMain>
        <BookDescription product={data?.product as Product} />
        <EditorExample buttonLink={book(data?.product.bookID, '')} />
        <Collaborate />
        <Gift />
        <Example />
        <BookTypes
          buttonText="Begin nu"
          buttonLink={book(data?.product.bookID, '')}
        />
        <GoodToKnow />
        <Reviews />
      </StyledMain>
      <Footer />
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const productBySlug = await ssrGetProductBySlug.getServerPage(
    { variables: { slug: ctx.params?.slug?.toString() || '' } },
    ctx,
  );
  const bookId = productBySlug.props.data?.product.bookID;
  const spreads = await ssrGetCollaborativeSpreads.getServerPage(
    { variables: { bookId } },
    ctx,
  );
  const isMultiUser = !!spreads.props.data.book.spreads.find(
    (spread) => spread.isCollaborative,
  );
  const extendedData = {
    ...productBySlug,
    props: {
      ...productBySlug.props,
      data: {
        ...productBySlug.props.data,
        product: {
          ...productBySlug.props.data.product,
          isMultiUser,
        },
      },
    },
  };

  return extendedData;
};

export default ProductDetails;
